import { CustomerSubscription, ProfileWithSubscription } from "@glamcorner/gc-types/generated/types";
import { BoxHistoryResponseBody } from "@glamcorner/gc-types/lib/boxHistory";
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { ClientServerError, NoResponseBodyError } from "../server/errors";
import {
  V2CreateSubscriptionRequest,
  V2CreateSubscriptionResponse,
  V2GetSubscriptionDropinUiToken,
  V2SubscriptionValidateCouponCodeRequest,
  V2SubscriptionValidateCouponCodeResponse,
} from "../common/communicationTypes";

interface BodyResponse<T> {
  body: T;
}

const createAxios = () => require("./utils/axiosApi").default;

export default class ServerGateway {
  private api: AxiosInstance;

  constructor(api: AxiosInstance = createAxios()) {
    this.api = api;
    (window as any)['serverGateway'] = this;
  }

  async fetchProfile(): Promise<ProfileWithSubscription> {
    return this.request<ProfileWithSubscription>("get", "/gc-api/v1/profile");
  }

  async fetchCustomerSubscription(): Promise<CustomerSubscription> {
    return this.request<CustomerSubscription>("get", "/api/v2/subscriptions");
  }

  async fetchDropInUiClientToken(): Promise<any> {
    return this.request<V2GetSubscriptionDropinUiToken>("get", "/api/v2/subscriptions/dropinUiToken");
  }

  async validateCouponCode(
    data: V2SubscriptionValidateCouponCodeRequest
  ): Promise<V2SubscriptionValidateCouponCodeResponse["body"]> {
    return this.request("post", "/api/v2/subscriptions/validateCouponCode", {
      data,
      // Because endpoint returns 404 if coupon not found,
      // Will throw exception instead of giving us back the response
      validateStatus: (_status) => true,
    });
  }

  async updateSubscriptionPaymentMethod(paymentMethodNonce: string): Promise<Response> {
    return this.request<Response>("put", "/api/v2/subscriptions", {
      data: { paymentMethodNonce },
    });
  }

  async createSubscription(data: V2CreateSubscriptionRequest): Promise<V2CreateSubscriptionResponse> {
    return this.request<V2CreateSubscriptionResponse>(
      "post",
      "/api/v2/subscription-signup/create",
      { data: data },
      false
    );
  }

  async getBoxHistory(limit: number, offset: number): Promise<BoxHistoryResponseBody> {
    return this.request<BoxHistoryResponseBody>(
      "get",
      `/api/v2/box-history?limit=${limit}&offset=${offset}`,
      {},
      false
    );
  }

  async usePaymentMethod(paymentMethodToken: string): Promise<BoxHistoryResponseBody> {
    return this.request<any>(
      "get",
      `/api/v2/use-payment-method/${paymentMethodToken}`,
      {},
      false
    );
  }

  private async request<T>(
    httpMethod: "get" | "put" | "post",
    url: string,
    config?: AxiosRequestConfig,
    unwrapBody = true
  ): Promise<T> {
    let response: AxiosResponse;
    try {
      response = await this.api.request<BodyResponse<T>>({
        ...(config || {}),
        url,
        method: httpMethod,
      });
    } catch (error) {
      console.error(error);
      if (error.response) {
        throw new ClientServerError(error, error.response.status);
      }
      throw new ClientServerError(error);
    }

    if (unwrapBody) {
      if (!response.data.body) {
        throw new NoResponseBodyError(response.data);
      }

      return response.data.body;
    } else {
      return response.data;
    }
  }
}
